import { CacheUpdateEvent } from '@/rec-cache/rec-cache-events'
import { Iris } from '@/rec-cache/rec-cache-types'
import { RebuildScheduler } from './rec-cache-rebuild-policy'

/**
 * AssetPathSelection represents a flat table of asset paths with lazy rebuild.
 * Each row is anchored on an equipment individual, with one row per point
 * (or one row with empty point if no points exist for that equipment).
 */
class AssetPathSelection {
  constructor(selector, refreshFn) {
    this.selector = selector
    this.refreshFn = refreshFn

    this._rows = []
    this._isDirty = true
    this._isBuilding = false
    this._isBuilt = false
    this._lastDurationMs = 0 // Cost of the most recent rebuild, in ms
    this._lastRevision = -1 // Cache revision these rows were built from

    // Set by the bridge so a view can show that a rebuild is under way.
    this.onRunningChange = null

    this._scheduler = new RebuildScheduler({
      run: () => {
        // See the equivalent skip in TreeSelection: an unchanged cache would
        // rebuild the table that is already rendered.
        const revision = this.selector.cache.revision
        if (this._isBuilt && revision === this._lastRevision) {
          this._isDirty = false
          return
        }
        this._rebuild()
        this._lastRevision = revision
      },
      isSyncing: () => !this.selector.cache.isSynced,
      lastDurationMs: () => this._lastDurationMs,
      onStateChange: (running) => {
        if (this.onRunningChange) this.onRunningChange(running)
      },
    })
  }

  /**
   * Register a viewer of this selection. Rebuilds only happen while at least
   * one viewer is registered — an unobserved selection stays dirty and
   * catches up the moment someone observes it.
   *
   * Returns an unsubscribe function; use it rather than tracking the pairing
   * by hand, since a missed release leaks a viewer and the selection then
   * rebuilds for nobody forever.
   */
  observe() {
    return this._scheduler.observe()
  }

  get isObserved() {
    return this._scheduler.isObserved
  }

  /**
   * Data changed. Rebuilds if observed, at a cadence the scheduler derives
   * from what the last rebuild cost. Unlike rebuild(), this may do nothing
   * now and rebuild later — or never, if nobody is looking.
   */
  requestRebuild() {
    this._isDirty = true
    this._scheduler.request()
  }

  get rows() {
    if (this._isDirty && !this._isBuilding) {
      this._rebuild()
    }
    return this._rows
  }

  get isBuilt() {
    return this._isBuilt && !this._isDirty
  }

  /** True from the announcement of a scheduled rebuild until it finishes. */
  get isRunning() {
    return this._scheduler.isRunning
  }

  markDirty() {
    this.requestRebuild()
  }

  _rebuild() {
    if (this._isBuilding) return
    this._isBuilding = true
    const _t0 = performance.now()

    const cache = this.selector.cache

    // Build individual lookup map for O(1) access
    const individualMap = new Map()
    cache.individuals.forEach((ind) => {
      individualMap.set(ind.id.value, ind)
    })

    const rows = []

    this.selector.equipmentIds.forEach((eqId) => {
      const equipment = individualMap.get(eqId)
      if (!equipment) return

      // Resolve spatial path upward from equipment
      const spatial = this._resolveSpatialPath(eqId, individualMap, cache)

      // Resolve system (may also contribute spatial context)
      const system = this._resolveSystem(eqId, individualMap, cache)

      // If system found but spatial is incomplete, try resolving spatial from system
      if (system) {
        this._resolveSpatialFromNode(
          system.id.value,
          individualMap,
          cache,
          spatial
        )
      }

      // Resolve points downward
      const pointIds = cache
        .queryEdges({ target: eqId, label: Iris.brickIsPointOf })
        .map((e) => e.source.value)

      if (pointIds.length === 0) {
        // One row for equipment with no points
        rows.push(this._buildRow(equipment, spatial, system, null))
      } else {
        // One row per point
        pointIds.forEach((pointId) => {
          const point = individualMap.get(pointId)
          if (point) {
            rows.push(this._buildRow(equipment, spatial, system, point))
          }
        })
      }
    })

    // Sort left-to-right by column priority
    const sortKeys = [
      'realestate',
      'building',
      'level',
      'room',
      'system',
      'equipment',
      'point',
    ]
    rows.sort((a, b) => {
      for (const key of sortKeys) {
        const av = a[key] || ''
        const bv = b[key] || ''
        const cmp = av.localeCompare(bv)
        if (cmp !== 0) return cmp
      }
      return 0
    })

    this._rows = rows
    this._isDirty = false
    this._isBuilding = false
    this._isBuilt = true

    const _buildMs = performance.now() - _t0

    // Publishing blocks the same main thread as the build — see the note in
    // TreeSelection._rebuild(). Timed here for the same reason.
    const _p0 = performance.now()
    if (this.refreshFn) {
      this.refreshFn(this._rows)
    }
    const _publishMs = performance.now() - _p0

    this._lastDurationMs = _buildMs + _publishMs
    console.log(
      `AssetPathSelection: rebuilt ${rows.length} rows from ${
        this.selector.equipmentIds.size
      } equipment in ${this._lastDurationMs.toFixed(
        0
      )}ms — build ${_buildMs.toFixed(0)}ms, publish ${_publishMs.toFixed(0)}ms`
    )
  }

  /**
   * Walk upward from equipment through spatial hierarchy.
   * Checks multiple relationship types:
   * - locatedIn (equipment → space)
   * - isLocationOf (space → equipment)
   * - includes (space → equipment)
   */
  _resolveSpatialPath(eqId, individualMap, cache) {
    const result = {
      room: null,
      level: null,
      building: null,
      realestate: null,
    }

    const spaceId = this._findSpatialParent(eqId, individualMap, cache)
    if (!spaceId) return result

    const space = individualMap.get(spaceId)
    if (!space) return result

    this._classifySpatial(space, result)
    this._walkUp(spaceId, individualMap, cache, result)

    return result
  }

  /**
   * Find the spatial parent of a node by checking all relevant relationship types.
   */
  _findSpatialParent(nodeId, individualMap, cache) {
    const types = this.selector.typeCategories

    // 1. locatedIn (outgoing from node)
    const locatedIn = this._findTarget(cache, nodeId, Iris.recLocatedIn)
    if (locatedIn) return locatedIn

    // 2. isLocationOf (incoming to node)
    const isLocationOf = this._findSource(cache, nodeId, Iris.recIsLocationOf)
    if (isLocationOf) return isLocationOf

    // 3. includes (incoming to node) — but only from spatial nodes, not systems
    //    Uses the broad spaceTypes set to recognize any Space subclass
    //    (building, level, room, outdoor space, zone, etc.)
    const includers = cache
      .queryEdges({ target: nodeId, label: Iris.recIncludes })
      .map((e) => e.source.value)
    for (const includerId of includers) {
      const includer = individualMap.get(includerId)
      if (!includer) continue
      const tv = includer.type.value
      if (types.spaceTypes.has(tv) || types.realestateTypes.has(tv)) {
        return includerId
      }
    }

    return null
  }

  /**
   * Resolve spatial context starting from a system node.
   * Checks isLocationOf incoming (Building -isLocationOf-> System)
   * and includes incoming from spatial nodes.
   * Only fills in slots that are still null.
   */
  _resolveSpatialFromNode(nodeId, individualMap, cache, result) {
    // isLocationOf incoming: Building -isLocationOf-> System
    let spaceId = this._findSource(cache, nodeId, Iris.recIsLocationOf)

    // Also check includes from spatial nodes
    if (!spaceId) {
      spaceId = this._findSpatialIncluder(nodeId, individualMap, cache)
    }

    // Also check locatedIn outgoing from node
    if (!spaceId) {
      spaceId = this._findTarget(cache, nodeId, Iris.recLocatedIn)
    }

    if (!spaceId) return

    const space = individualMap.get(spaceId)
    if (!space) return

    this._classifySpatial(space, result)
    this._walkUp(spaceId, individualMap, cache, result)
  }

  /**
   * Walk upward through spatial hierarchy via isPartOf / includes.
   * Only uses hierarchical relationships, not locatedIn/isLocationOf.
   */
  _walkUp(nodeId, individualMap, cache, result) {
    const visited = new Set()
    let currentId = nodeId

    while (currentId && !visited.has(currentId)) {
      visited.add(currentId)

      // Try isPartOf (outgoing from child → parent)
      let parentId = this._findTarget(cache, currentId, Iris.recIsPartOf)

      // Try includes (incoming: parent → child) — only spatial parents
      if (!parentId) {
        parentId = this._findSpatialIncluder(
          currentId,
          individualMap,
          cache,
          visited
        )
      }

      if (!parentId) break

      const parent = individualMap.get(parentId)
      if (!parent) break

      this._classifySpatial(parent, result)
      currentId = parentId
    }
  }

  /**
   * Find a spatial node that includes the given node via includes edge.
   * Uses the broad spaceTypes set to recognize any Space subclass.
   */
  _findSpatialIncluder(nodeId, individualMap, cache, visited) {
    const types = this.selector.typeCategories
    const includers = cache
      .queryEdges({ target: nodeId, label: Iris.recIncludes })
      .map((e) => e.source.value)
    for (const includerId of includers) {
      if (visited && visited.has(includerId)) continue
      const includer = individualMap.get(includerId)
      if (!includer) continue
      const tv = includer.type.value
      if (types.spaceTypes.has(tv) || types.realestateTypes.has(tv)) {
        return includerId
      }
    }
    return null
  }

  /**
   * Classify a spatial individual into the appropriate result slot.
   */
  _classifySpatial(individual, result) {
    const typeValue = individual.type.value
    const types = this.selector.typeCategories

    if (types.roomTypes.has(typeValue) && !result.room) {
      result.room = individual
    } else if (types.levelTypes.has(typeValue) && !result.level) {
      result.level = individual
    } else if (types.buildingTypes.has(typeValue) && !result.building) {
      result.building = individual
    } else if (types.realestateTypes.has(typeValue) && !result.realestate) {
      result.realestate = individual
    }
  }

  /**
   * Resolve system for equipment via includes (system → equipment) or feeds.
   */
  _resolveSystem(eqId, individualMap, cache) {
    const types = this.selector.typeCategories

    // Check all includers for a system type
    const includers = cache
      .queryEdges({ target: eqId, label: Iris.recIncludes })
      .map((e) => e.source.value)
    for (const includerId of includers) {
      const includer = individualMap.get(includerId)
      if (includer && types.systemTypes.has(includer.type.value)) {
        return includer
      }
    }

    // Check feeds (system → equipment)
    const feeders = cache
      .queryEdges({ target: eqId, label: Iris.brickFeeds })
      .map((e) => e.source.value)
    for (const feederId of feeders) {
      const feeder = individualMap.get(feederId)
      if (feeder && types.systemTypes.has(feeder.type.value)) {
        return feeder
      }
    }

    return null
  }

  _findTarget(cache, sourceId, label) {
    const edges = cache.queryEdges({ source: sourceId, label })
    return edges.length > 0 ? edges[0].target.value : null
  }

  _findSource(cache, targetId, label) {
    const edges = cache.queryEdges({ target: targetId, label })
    return edges.length > 0 ? edges[0].source.value : null
  }

  _buildRow(equipment, spatial, system, point) {
    const row = {}

    if (spatial.realestate) {
      row.realestate = spatial.realestate.label.value
      row.realestateType = spatial.realestate.type.short
    }
    if (spatial.building) {
      row.building = spatial.building.label.value
      row.buildingType = spatial.building.type.short
    }
    if (spatial.level) {
      row.level = spatial.level.label.value
      row.levelType = spatial.level.type.short
    }
    if (spatial.room) {
      row.room = spatial.room.label.value
      row.roomType = spatial.room.type.short
    }
    if (system) {
      row.system = system.type.short + ` (${system.label.value})`
      row.systemType = system.type.short
    }
    if (equipment) {
      row.equipment = equipment.type.short + ` (${equipment.label.value})`
      row.equipmentType = equipment.type.short
    }
    if (point) {
      row.point = point.type.short
      row.pointType = point.type.short
    }

    return row
  }

  /** Rebuild now, observed or not — for explicit user actions. */
  rebuild() {
    this._isDirty = true
    this._scheduler.flush()
  }

  clear() {
    this._rows = []
    this._isDirty = true
    this._isBuilt = false
    if (this.refreshFn) {
      this.refreshFn(this._rows)
    }
  }
}

/**
 * AssetPathSelector accumulates equipment IDs and type categories during sync.
 * Passively listens to CacheUpdateEvent. Path building is triggered lazily
 * by AssetPathSelection when accessed.
 */
class AssetPathSelector {
  /**
   * @param {Object} dbs - { cache, ids }
   * @param {Object} typeCategories - Sets of type IRI values per category
   */
  constructor(dbs, typeCategories) {
    this.cache = dbs.cache
    this.ids = dbs.ids
    this.typeCategories = typeCategories

    // Track equipment individual IDs
    this.equipmentIds = new Set()

    this.isDirty = false
    this._states = []

    addEventListener(CacheUpdateEvent, this.update.bind(this))
  }

  /**
   * Called when an individual is added/updated in the cache.
   */
  update({ detail: individual }) {
    const typeValue = individual.type.value
    if (this.typeCategories.equipmentTypes.has(typeValue)) {
      this.equipmentIds.add(individual.id.value)
    }
    this.isDirty = true
  }

  /**
   * Rebuild equipment ID set from scratch.
   * Self-healing after hydration or sync.
   */
  rebuildEquipmentIds() {
    const prev = this.equipmentIds.size
    this.equipmentIds.clear()

    this.cache.individuals.forEach((individual) => {
      const typeValue = individual.type.value
      if (this.typeCategories.equipmentTypes.has(typeValue)) {
        this.equipmentIds.add(individual.id.value)
      }
    })

    console.log(
      `AssetPathSelector: rebuildEquipmentIds() — ${prev}→${this.equipmentIds.size}`
    )
  }

  createSelection(refreshFn = null) {
    const selection = new AssetPathSelection(this, refreshFn)
    this._states.push(selection)
    return selection
  }

  clear() {
    this.equipmentIds.clear()
    this.isDirty = false
    this._states.forEach((s) => s.clear())
  }
}

export { AssetPathSelector }
